import { addDays, format, parseISO } from 'date-fns';
import type { Granularity } from '@/hooks/useAnalytics';

export const COLORS = {
  // Navy / sage / soft crimson — clearer hues that sit with admin primary
  primary: {
    main: '#3D5A80',
    light: '#7A9BC4',
    dark: '#2C4260',
  },

  charts: {
    blue: '#3D5A80',
    blueDark: '#2C4260',
    // Document Processing — blue-teal
    tokenPink: '#2B8CA8',
    tokenPinkActive: '#216E86',
    // Case Partner — pink
    tokenBlue: '#E07A9A',
    tokenBlueActive: '#C45A7C',
    // Early Case Assessment — butter yellow
    tokenYellow: '#F0C75E',
    tokenYellowActive: '#D4A93F',
  },
};

// Utility function to fill gaps in time series data with zeros
// Uses UTC throughout to avoid off-by-one day mismatches with backend UTC dates
export const fillTimeSeriesGaps = <T extends { date: string }>(
  data: T[],
  dateFrom: Date,
  dateTo: Date,
  defaultValues: Omit<T, 'date'>,
  granularity: Granularity = 'day'
): T[] => {
  const filledData: T[] = [];
  const dataMap = new Map(data.map(d => [d.date, d]));

  // Construct dates in UTC to match backend date strings
  let currentDate = new Date(Date.UTC(dateFrom.getUTCFullYear(), dateFrom.getUTCMonth(), dateFrom.getUTCDate()));
  if (granularity === 'week') {
    // Manually compute start of ISO week (Monday) in UTC
    const day = currentDate.getUTCDay();
    const diff = (day === 0 ? -6 : 1) - day; // Monday = 1
    currentDate.setUTCDate(currentDate.getUTCDate() + diff);
  } else if (granularity === 'month') {
    currentDate.setUTCDate(1);
  }

  const endDate = new Date(Date.UTC(dateTo.getUTCFullYear(), dateTo.getUTCMonth(), dateTo.getUTCDate()));

  while (currentDate <= endDate) {
    const dateStr = currentDate.toISOString().slice(0, 10);
    const existing = dataMap.get(dateStr);

    if (existing) {
      filledData.push(existing);
    } else {
      filledData.push({ date: dateStr, ...defaultValues } as T);
    }

    if (granularity === 'week') {
      currentDate = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate() + 7));
    } else if (granularity === 'month') {
      currentDate = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() + 1, currentDate.getUTCDate()));
    } else {
      currentDate = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate() + 1));
    }
  }

  return filledData;
};

/** Monday–Sunday span for a week bucket, e.g. "1–7 Sep" or "29 Sep–5 Oct". */
const formatWeekRangeLabel = (dateStr: string): string => {
  const start = parseISO(dateStr);
  const end = addDays(start, 6);

  if (start.getFullYear() !== end.getFullYear()) {
    return `${format(start, 'd MMM yyyy')}–${format(end, 'd MMM yyyy')}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${format(start, 'd MMM')}–${format(end, 'd MMM')}`;
  }
  return `${format(start, 'd')}–${format(end, 'd MMM')}`;
};

// Format date label based on granularity
export const formatDateLabel = (dateStr: string, granularity: Granularity): string => {
  switch (granularity) {
    case 'week':
      return formatWeekRangeLabel(dateStr);
    case 'month':
      return format(parseISO(dateStr), 'MMM yyyy');
    default:
      return format(parseISO(dateStr), 'MMM d');
  }
};

// Get description suffix based on granularity
export const getGranularityLabel = (granularity: Granularity): string => {
  switch (granularity) {
    case 'week':
      return 'Weekly';
    case 'month':
      return 'Monthly';
    default:
      return 'Daily';
  }
};

// Format large numbers
export const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
};

// Custom tooltip styles for charts
export const customTooltipStyle = {
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  border: 'none',
  borderRadius: '12px',
  padding: '16px 20px',
  boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(229, 231, 235, 0.5)',
};

export const customTooltipLabelStyle = {
  fontWeight: 700,
  fontSize: '14px',
  color: '#111827',
  marginBottom: '10px',
  paddingBottom: '8px',
  borderBottom: '1px solid #E5E7EB',
};

export const customTooltipItemStyle = {
  fontSize: '13px',
  color: '#6b7280',
  padding: '2px 0',
};

export const PAGE_SIZE = 10;

// Calculate x-axis tick interval based on data length
export const calcTickInterval = (dataLength: number): number => {
  if (dataLength <= 7) return 0; // Show all ticks
  if (dataLength <= 14) return 1; // Show every other tick
  if (dataLength <= 30) return 2; // Show every 3rd tick
  return Math.floor(dataLength / 12); // Show ~12 ticks for longer ranges
};
